
      import { Gracket, generateTournamentWithByes } from '/src/index.ts';
      import '/src/style.css';

      // ========================================================================
      // Helper Functions
      // ========================================================================

      function addLog(containerId, message, type = 'info') {
        const container = document.getElementById(containerId);
        const entry = document.createElement('div');
        entry.className = `log-entry log-${type}`;
        entry.textContent = `[${new Date().toLocaleTimeString()}] ${message}`;
        container.appendChild(entry);
        container.scrollTop = container.scrollHeight;
      }

      function clearLog(containerId) {
        document.getElementById(containerId).innerHTML = '';
      }

      // ========================================================================
      // Demo 1: Byes Support
      // ========================================================================

      let byesBracket = null;

      function loadByesTournament(teamCount) {
        clearLog('byes-log');
        addLog('byes-log', `Loading ${teamCount}-team tournament...`, 'info');

        const teams = Array.from({ length: teamCount }, (_, i) => ({
          name: `Team ${String.fromCharCode(65 + i)}`,
          id: `team-${i}`,
          seed: i + 1
        }));

        const data = generateTournamentWithByes(teams, 'top-seeds');
        const byesNeeded = Math.pow(2, Math.ceil(Math.log2(teamCount))) - teamCount;

        addLog('byes-log', `Generated tournament with ${byesNeeded} bye(s)`, 'success');
        addLog('byes-log', `Top ${byesNeeded} seed(s) receive automatic bye(s)`, 'info');

        if (byesBracket) {
          byesBracket.destroy();
        }

        byesBracket = new Gracket('#byes-bracket', {
          src: data,
          byeLabel: 'BYE',
          roundLabels: ['Round 1', 'Round 2', 'Semifinals', 'Finals', 'Champion']
        });

        addLog('byes-log', 'Bracket rendered with bye visualizations', 'success');
      }

      document.getElementById('load-byes-6').addEventListener('click', () => loadByesTournament(6));
      document.getElementById('load-byes-5').addEventListener('click', () => loadByesTournament(5));
      document.getElementById('load-byes-7').addEventListener('click', () => loadByesTournament(7));
      document.getElementById('load-byes-10').addEventListener('click', () => loadByesTournament(10));

      // Load initial
      loadByesTournament(6);

      // ========================================================================
      // Demo 2: Interactive Scoring & Auto-Generation
      // ========================================================================

      const initialScoringTeams = [
        { name: 'Warriors', id: 'warriors', seed: 1 },
        { name: 'Lakers', id: 'lakers', seed: 2 },
        { name: 'Celtics', id: 'celtics', seed: 3 },
        { name: 'Heat', id: 'heat', seed: 4 },
        { name: 'Bucks', id: 'bucks', seed: 5 },
        { name: 'Suns', id: 'suns', seed: 6 }
      ];

      let scoringBracket = null;
      let currentRound = 0;

      function initScoringBracket() {
        clearLog('scoring-log');
        document.getElementById('stats-display').style.display = 'grid';
        
        const data = generateTournamentWithByes(initialScoringTeams, 'top-seeds');
        
        if (scoringBracket) {
          scoringBracket.destroy();
        }

        scoringBracket = new Gracket('#scoring-bracket', {
          src: data,
          byeLabel: 'BYE',
          roundLabels: ['Round 1', 'Semifinals', 'Finals', 'Champion'],
          
          onScoreUpdate: (r, g, t, score) => {
            addLog('scoring-log', `Score entered: Round ${r + 1}, Game ${g + 1}, Team ${t + 1}: ${score} pts`, 'info');
            updateStats();
          },
          
          onRoundComplete: (r) => {
            addLog('scoring-log', `Round ${r + 1} completed!`, 'success');
            const advancing = scoringBracket.getAdvancingTeams(r);
            addLog('scoring-log', `Advancing: ${advancing.map(t => t.name).join(', ')}`, 'success');
            updateStats();
          },
          
          onRoundGenerated: (r, roundData) => {
            addLog('scoring-log', `Round ${r + 1} generated with ${roundData.length} game(s)`, 'success');
            updateStats();
          }
        });

        currentRound = 0;
        updateStats();
        addLog('scoring-log', 'Tournament initialized', 'success');
      }

      function updateStats() {
        if (!scoringBracket) return;
        
        const stats = scoringBracket.getStatistics();
        const data = scoringBracket.getData();
        
        // Find current round
        let current = 0;
        for (let i = 0; i < data.length; i++) {
          if (!scoringBracket.isRoundComplete(i)) {
            current = i;
            break;
          }
        }
        
        document.getElementById('stat-current-round').textContent = current + 1;
        document.getElementById('stat-completion').textContent = `${stats.completionPercentage}%`;
        document.getElementById('stat-avg-score').textContent = stats.averageScore ? stats.averageScore.toFixed(1) : 'N/A';
        document.getElementById('stat-byes').textContent = stats.byeCount;
      }

      document.getElementById('enter-scores').addEventListener('click', () => {
        const data = scoringBracket.getData();
        
        // Find first incomplete round
        for (let r = 0; r < data.length; r++) {
          if (scoringBracket.isRoundComplete(r)) continue;
          
          const round = data[r];
          for (let g = 0; g < round.length; g++) {
            const game = round[g];
            if (game.length === 2) {
              // Regular match - add random scores
              if (game[0].score === undefined) {
                const score1 = Math.floor(Math.random() * 30) + 90;
                const score2 = Math.floor(Math.random() * 30) + 85;
                scoringBracket.updateScore(r, g, 0, score1);
                scoringBracket.updateScore(r, g, 1, score2);
                addLog('scoring-log', `Random scores: ${game[0].name} ${score1} - ${game[1].name} ${score2}`, 'info');
              }
            }
          }
          break;
        }
      });

      document.getElementById('advance-round').addEventListener('click', () => {
        const data = scoringBracket.getData();
        
        for (let r = 0; r < data.length; r++) {
          if (scoringBracket.isRoundComplete(r) && r < data.length - 1) {
            // Check if next round exists and is empty
            if (data[r + 1].every(game => game.every(team => !team.score))) {
              try {
                scoringBracket.advanceRound(r, {
                  tieBreaker: 'higher-seed',
                  createRounds: true
                });
                addLog('scoring-log', `Advanced from Round ${r + 1} to Round ${r + 2}`, 'success');
                return;
              } catch (e) {
                addLog('scoring-log', `Error: ${e.message}`, 'error');
                return;
              }
            }
          } else if (!scoringBracket.isRoundComplete(r)) {
            addLog('scoring-log', `Round ${r + 1} is not complete yet`, 'warning');
            return;
          }
        }
        
        addLog('scoring-log', 'No rounds to advance', 'warning');
      });

      document.getElementById('auto-generate').addEventListener('click', () => {
        try {
          scoringBracket.autoGenerateTournament({
            tieBreaker: 'higher-seed',
            onRoundGenerated: (idx, data) => {
              addLog('scoring-log', `Auto-generated Round ${idx + 1}`, 'success');
            }
          });
          addLog('scoring-log', 'Tournament auto-generation complete!', 'success');
        } catch (e) {
          addLog('scoring-log', `Error: ${e.message}`, 'error');
        }
      });

      document.getElementById('reset-scores').addEventListener('click', () => {
        initScoringBracket();
      });

      initScoringBracket();

      // ========================================================================
      // Demo 3: Reporting
      // ========================================================================

      const reportingTeams = [
        { name: 'Warriors', id: 'warriors', seed: 1, score: 105 },
        { name: 'Thunder', id: 'thunder', seed: 8, score: 92 },
        { name: 'Lakers', id: 'lakers', seed: 2, score: 110 },
        { name: 'Rockets', id: 'rockets', seed: 7, score: 98 },
        { name: 'Celtics', id: 'celtics', seed: 3, score: 108 },
        { name: 'Pacers', id: 'pacers', seed: 6, score: 105 },
        { name: 'Heat', id: 'heat', seed: 4, score: 112 },
        { name: 'Nets', id: 'nets', seed: 5, score: 98 }
      ];

      const reportingData = [
        [
          [
            { ...reportingTeams[0] },
            { ...reportingTeams[1] }
          ],
          [
            { ...reportingTeams[2] },
            { ...reportingTeams[3] }
          ],
          [
            { ...reportingTeams[4] },
            { ...reportingTeams[5] }
          ],
          [
            { ...reportingTeams[6] },
            { ...reportingTeams[7] }
          ]
        ],
        [
          [
            { name: 'Warriors', id: 'warriors', seed: 1, score: 118 },
            { name: 'Lakers', id: 'lakers', seed: 2, score: 105 }
          ],
          [
            { name: 'Celtics', id: 'celtics', seed: 3, score: 108 },
            { name: 'Heat', id: 'heat', seed: 4, score: 102 }
          ]
        ],
        [
          [
            { name: 'Warriors', id: 'warriors', seed: 1, score: 120 },
            { name: 'Celtics', id: 'celtics', seed: 3, score: 115 }
          ]
        ],
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1 }]
        ]
      ];

      const reportingBracket = new Gracket('#reporting-bracket', {
        src: reportingData,
        roundLabels: ['Quarterfinals', 'Semifinals', 'Finals', 'Champion']
      });

      document.getElementById('show-advancing').addEventListener('click', () => {
        const container = document.getElementById('advancing-teams');
        container.style.display = 'flex';
        container.innerHTML = '';
        
        document.getElementById('report-output').style.display = 'none';
        
        const advancing = reportingBracket.getAdvancingTeams(0);
        advancing.forEach(team => {
          const chip = document.createElement('div');
          chip.className = 'team-chip';
          chip.textContent = `${team.name} (Seed ${team.seed})`;
          container.appendChild(chip);
        });
      });

      document.getElementById('show-team-history').addEventListener('click', () => {
        const history = reportingBracket.getTeamHistory('warriors');
        
        document.getElementById('advancing-teams').style.display = 'none';
        const output = document.getElementById('report-output');
        output.style.display = 'block';
        
        let report = `=== ${history.team.name} Tournament History ===\n\n`;
        report += `Final Record: ${history.wins}W - ${history.losses}L\n`;
        report += `Final Placement: ${history.finalPlacement || 'In Progress'}\n\n`;
        report += `Match-by-Match:\n`;
        
        history.matches.forEach((match, i) => {
          const result = match.won ? '✓ WIN ' : '✗ LOSS';
          const opponent = match.isBye ? 'BYE' : match.opponent?.name;
          const scoreDetail = match.score && match.opponentScore 
            ? ` (${match.score}-${match.opponentScore})` 
            : '';
          report += `  ${i + 1}. ${match.roundLabel}: ${result} vs ${opponent}${scoreDetail}\n`;
        });
        
        output.textContent = report;
      });

      document.getElementById('generate-text-report').addEventListener('click', () => {
        document.getElementById('advancing-teams').style.display = 'none';
        const output = document.getElementById('report-output');
        output.style.display = 'block';
        
        const report = reportingBracket.generateReport({
          format: 'text',
          includeScores: true,
          includeStatistics: true
        });
        
        output.textContent = report;
      });

      document.getElementById('generate-json-report').addEventListener('click', () => {
        document.getElementById('advancing-teams').style.display = 'none';
        const output = document.getElementById('report-output');
        output.style.display = 'block';
        
        const report = reportingBracket.generateReport({ format: 'json' });
        output.textContent = JSON.stringify(report, null, 2);
      });

      document.getElementById('show-statistics').addEventListener('click', () => {
        const stats = reportingBracket.getStatistics();
        
        document.getElementById('advancing-teams').style.display = 'none';
        const output = document.getElementById('report-output');
        output.style.display = 'block';
        
        let report = '=== Tournament Statistics ===\n\n';
        report += `Participants: ${stats.participantCount}\n`;
        report += `Total Rounds: ${stats.totalRounds}\n`;
        report += `Byes: ${stats.byeCount}\n`;
        report += `Average Score: ${stats.averageScore?.toFixed(1) || 'N/A'}\n`;
        report += `Completion: ${stats.completionPercentage}%\n`;
        
        if (stats.highestScore) {
          report += `\nHighest Score:\n`;
          report += `  ${stats.highestScore.team.name} scored ${stats.highestScore.score}\n`;
          report += `  in round ${stats.highestScore.round + 1}\n`;
        }
        
        output.textContent = report;
      });

      // ========================================================================
      // Demo 4: Complete Interactive Tournament
      // ========================================================================

      let completeBracket = null;

      document.getElementById('complete-start').addEventListener('click', () => {
        clearLog('complete-log');
        
        const teams = [
          { name: 'Warriors', id: 'warriors', seed: 1 },
          { name: 'Lakers', id: 'lakers', seed: 2 },
          { name: 'Celtics', id: 'celtics', seed: 3 },
          { name: 'Heat', id: 'heat', seed: 4 },
          { name: 'Bucks', id: 'bucks', seed: 5 },
          { name: 'Suns', id: 'suns', seed: 6 },
          { name: 'Nets', id: 'nets', seed: 7 }
        ];
        
        const data = generateTournamentWithByes(teams, 'top-seeds');
        
        if (completeBracket) {
          completeBracket.destroy();
        }
        
        completeBracket = new Gracket('#complete-bracket', {
          src: data,
          byeLabel: 'BYE',
          roundLabels: ['Round 1', 'Semifinals', 'Finals', 'Champion'],
          
          onScoreUpdate: (r, g, t, score) => {
            addLog('complete-log', `Score: ${score}`, 'info');
          },
          
          onRoundComplete: (r) => {
            const advancing = completeBracket.getAdvancingTeams(r);
            addLog('complete-log', `Round ${r + 1} complete! Advancing: ${advancing.map(t => t.name).join(', ')}`, 'success');
          }
        });
        
        addLog('complete-log', '7-team tournament started with byes for top seed', 'success');
      });

      document.getElementById('complete-score').addEventListener('click', () => {
        if (!completeBracket) {
          addLog('complete-log', 'Start a tournament first!', 'warning');
          return;
        }
        
        const data = completeBracket.getData();
        
        // Enter scores for first incomplete match
        for (let r = 0; r < data.length; r++) {
          if (completeBracket.isRoundComplete(r)) continue;
          
          const round = data[r];
          for (let g = 0; g < round.length; g++) {
            const game = round[g];
            if (game.length === 2 && !game[0].score) {
              const score1 = Math.floor(Math.random() * 30) + 90;
              const score2 = Math.floor(Math.random() * 30) + 85;
              
              completeBracket.updateScore(r, g, 0, score1);
              completeBracket.updateScore(r, g, 1, score2);
              
              addLog('complete-log', `Match scored: ${game[0].name} ${score1} - ${game[1].name} ${score2}`, 'info');
              
              // Auto-advance if round complete
              if (completeBracket.isRoundComplete(r)) {
                completeBracket.advanceRound(r, {
                  tieBreaker: 'higher-seed',
                  createRounds: true
                });
              }
              return;
            }
          }
        }
        
        addLog('complete-log', 'All matches have scores!', 'info');
      });

      document.getElementById('complete-report').addEventListener('click', () => {
        if (!completeBracket) {
          addLog('complete-log', 'Start a tournament first!', 'warning');
          return;
        }
        
        const report = completeBracket.generateReport({
          format: 'text',
          includeScores: true,
          includeStatistics: true
        });
        
        addLog('complete-log', '\\n' + report, 'success');
      });

      // ========================================================================
      // Demo 5: Double Elimination Bracket
      // ========================================================================

      // 8-team double elimination tournament data
      const doubleElimTeams = [
        { name: 'Warriors', id: 'warriors', seed: 1 },
        { name: 'Lakers', id: 'lakers', seed: 2 },
        { name: 'Celtics', id: 'celtics', seed: 3 },
        { name: 'Heat', id: 'heat', seed: 4 },
        { name: 'Bucks', id: 'bucks', seed: 5 },
        { name: 'Suns', id: 'suns', seed: 6 },
        { name: 'Nets', id: 'nets', seed: 7 },
        { name: 'Nuggets', id: 'nuggets', seed: 8 }
      ];

      // Winners Bracket (8 teams -> 4 -> 2 -> 1 champion)
      const winnersBracketData = [
        // Round 1: Quarterfinals (4 matches)
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1, score: 105 }, { name: 'Nuggets', id: 'nuggets', seed: 8, score: 92 }],
          [{ name: 'Lakers', id: 'lakers', seed: 2, score: 98 }, { name: 'Nets', id: 'nets', seed: 7, score: 110 }],
          [{ name: 'Celtics', id: 'celtics', seed: 3, score: 108 }, { name: 'Suns', id: 'suns', seed: 6, score: 102 }],
          [{ name: 'Heat', id: 'heat', seed: 4, score: 95 }, { name: 'Bucks', id: 'bucks', seed: 5, score: 112 }]
        ],
        // Round 2: Semifinals (2 matches)
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1, score: 118 }, { name: 'Nets', id: 'nets', seed: 7, score: 105 }],
          [{ name: 'Celtics', id: 'celtics', seed: 3, score: 115 }, { name: 'Bucks', id: 'bucks', seed: 5, score: 108 }]
        ],
        // Round 3: Finals (1 match)
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1, score: 120 }, { name: 'Celtics', id: 'celtics', seed: 3, score: 115 }]
        ],
        // Round 4: Champion
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1 }]
        ]
      ];

      // Losers Bracket (losers from winners bracket feed in)
      // Structure: Round 1 losers play each other, then winners play Round 2 losers, etc.
      const losersBracketData = [
        // Round 1: First round losers (4 teams -> 2)
        [
          [{ name: 'Nuggets', id: 'nuggets', seed: 8, score: 88 }, { name: 'Lakers', id: 'lakers', seed: 2, score: 95 }],
          [{ name: 'Suns', id: 'suns', seed: 6, score: 102 }, { name: 'Heat', id: 'heat', seed: 4, score: 98 }]
        ],
        // Round 2: Losers from Winners Round 2 + Winners from Losers Round 1 (4 teams -> 2)
        [
          [{ name: 'Lakers', id: 'lakers', seed: 2, score: 92 }, { name: 'Nets', id: 'nets', seed: 7, score: 105 }],
          [{ name: 'Suns', id: 'suns', seed: 6, score: 98 }, { name: 'Bucks', id: 'bucks', seed: 5, score: 110 }]
        ],
        // Round 3: Semifinals (2 teams -> 1)
        [
          [{ name: 'Nets', id: 'nets', seed: 7, score: 108 }, { name: 'Bucks', id: 'bucks', seed: 5, score: 112 }]
        ],
        // Round 4: Finals (loser plays winner from previous round)
        [
          [{ name: 'Nets', id: 'nets', seed: 7, score: 115 }, { name: 'Celtics', id: 'celtics', seed: 3, score: 118 }]
        ],
        // Round 5: Losers Bracket Champion
        [
          [{ name: 'Celtics', id: 'celtics', seed: 3 }]
        ]
      ];

      // Grand Finals: Winners Bracket Champion vs Losers Bracket Champion
      const grandFinalsData = [
        // Grand Finals Match 1
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1, score: 125 }, { name: 'Celtics', id: 'celtics', seed: 3, score: 120 }]
        ],
        // Grand Finals Match 2 (if needed - Warriors already won, so this is just for display)
        [
          [{ name: 'Warriors', id: 'warriors', seed: 1 }]
        ]
      ];

      // Initialize Winners Bracket
      const winnersBracket = new Gracket('#winners-bracket', {
        src: winnersBracketData,
        roundLabels: ['Quarterfinals', 'Semifinals', 'Finals', 'Champion'],
        canvasLineColor: '#2ea043'
      });

      // Initialize Losers Bracket
      const losersBracket = new Gracket('#losers-bracket', {
        src: losersBracketData,
        roundLabels: ['Round 1', 'Round 2', 'Semifinals', 'Finals', 'Champion'],
        canvasLineColor: '#f85149'
      });

      // Initialize Grand Finals
      const grandFinalsBracket = new Gracket('#grand-finals-bracket', {
        src: grandFinalsData,
        roundLabels: ['Grand Finals', 'Champion'],
        canvasLineColor: '#ffd700'
      });
    